/**
 * useAutoResize Hook (Story 5.7, Task 2.2)
 *
 * ResizeObserver-based SAFETY NET for text block auto-height.
 * The primary sizing source is the deterministic `estimateTextRowSpan()`.
 * This hook validates the heuristic and bumps rowSpan +1 if content overflows.
 *
 * Guards against infinite resize loops:
 * - Compares newRowSpan to currentRowSpan before updating
 * - Uses isResizing ref lock to prevent cascading updates
 * - Debounces observer callback 200ms
 * - Defers first observation to after initial paint (requestAnimationFrame)
 * - Max rowSpan capped at 8
 */

import { useEffect, useRef, useCallback } from "react";
import { ROW_HEIGHT_UNIT } from "./gridUtils";

const MAX_ROW_SPAN = 8;
const DEBOUNCE_MS = 200;

interface UseAutoResizeOptions {
  /** Current rowSpan of the widget */
  currentRowSpan: number;
  /** Whether auto-size is enabled for this widget */
  enabled: boolean;
  /** Callback when rowSpan needs to increase */
  onAutoResize: (newRowSpan: number) => void;
}

/**
 * Observes a DOM element and calls onAutoResize if content overflows
 * the current rowSpan allocation.
 *
 * Returns a ref callback to attach to the content container.
 */
export function useAutoResize({ currentRowSpan, enabled, onAutoResize }: UseAutoResizeOptions) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isResizing = useRef(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const checkOverflow = useCallback(() => {
    if (!containerRef.current || !enabled || isResizing.current) return;

    const el = containerRef.current;
    const scrollHeight = el.scrollHeight;
    const availableHeight = currentRowSpan * ROW_HEIGHT_UNIT;

    // Content overflows — bump rowSpan by 1
    if (scrollHeight > availableHeight && currentRowSpan < MAX_ROW_SPAN) {
      const newRowSpan = Math.min(currentRowSpan + 1, MAX_ROW_SPAN);
      if (newRowSpan !== currentRowSpan) {
        isResizing.current = true;
        onAutoResize(newRowSpan);
        // Release lock after a frame to let the layout settle
        requestAnimationFrame(() => {
          isResizing.current = false;
        });
      }
    }
  }, [currentRowSpan, enabled, onAutoResize]);

  useEffect(() => {
    if (!enabled || !containerRef.current) return;

    const el = containerRef.current;

    const observer = new ResizeObserver(() => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(checkOverflow, DEBOUNCE_MS);
    });

    // Defer first observation to after initial paint
    requestAnimationFrame(() => {
      if (el) {
        observer.observe(el);
        checkOverflow();
      }
    });

    return () => {
      observer.disconnect();
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [enabled, checkOverflow]);

  return containerRef;
}
